import { urls, urlsWrapper } from 'config';
import { isEqual } from 'es-toolkit/compat';
import { commonGet, commonPost } from './api';

export const mapbox = {
  SET_TOKEN: 'MAPBOX_SET_TOKEN',
  SET_TOKENS: 'MAPBOX_SET_TOKENS',
  SET_FIELDS_GEOJSON: 'MAPBOX_SET_FIELDS_GEOJSON',
  INSERT_FIELDS_GEOJSON: 'MAPBOX_INSERT_FIELDS_GEOJSON',
  SET_CURRENT_ITEM: 'MAPBOX_SET_CURRENT_ITEM',
  SET_ROUTE: 'MAPBOX_SET_ROUTE',
  SET_STYLE: 'MAPBOX_SET_STYLE',
  SET_BOUNDS: 'MAPBOX_SET_BOUNDS',
  SET_TOKEN_IS_PENDING: 'MAPBOX_SET_TOKEN_IS_PENDING',
  SET_FIELDS_GEOJSON_IS_PENDING: 'MAPBOX_SET_FIELDS_GEOJSON_IS_PENDING',
};

// reducer
const defaultState = {
  token: null,
  bingmapstoken: null,
  fieldsGeojson: {
    type: 'FeatureCollection',
    bbox: [],
    features: [],
  },
  route: null,
  currentItem: null,
  mapStyle: 0,
  bounds: null,
  tokenIsPending: false,
  geojsonIsPending: false,
};

let latestFieldsGeojsonRequestId = 0;

export default (state = defaultState, action) => {
  switch (action.type) {
    case mapbox.SET_TOKEN:
      return { ...state, token: action.token };

    case mapbox.SET_TOKENS:
      return {
        ...state,
        token: action.tokens.token,
        bingmapstoken: action.tokens.bingmapstoken,
      };

    case mapbox.SET_FIELDS_GEOJSON:
      return { ...state, fieldsGeojson: action.geojson };

    case mapbox.INSERT_FIELDS_GEOJSON: {
      const insertedId = action.feature?.properties?.field_id;
      const features = !insertedId
        ? state.fieldsGeojson.features
        : state.fieldsGeojson.features.filter(
            (feature) => feature?.properties?.field_id !== insertedId
          );

      return {
        ...state,
        fieldsGeojson: {
          type: state.fieldsGeojson.type || 'FeatureCollection',
          bbox: state.fieldsGeojson.bbox || [],
          features: [...(features || []), action.feature],
        },
      };
    }

    case mapbox.SET_CURRENT_ITEM:
      return { ...state, currentItem: action.item };

    case mapbox.SET_ROUTE:
      return { ...state, route: action.item };

    case mapbox.SET_STYLE:
      return { ...state, mapStyle: action.index };

    case mapbox.SET_BOUNDS:
      return { ...state, bounds: action.bounds };

    case mapbox.SET_TOKEN_IS_PENDING:
      return { ...state, tokenIsPending: action.tokenIsPending };

    case mapbox.SET_FIELDS_GEOJSON_IS_PENDING:
      return { ...state, geojsonIsPending: action.geojsonIsPending };

    default:
      return state;
  }
};

// actions
export function setToken(token) {
  return {
    type: mapbox.SET_TOKEN,
    token,
  };
}

export function setTokens(tokens) {
  return {
    type: mapbox.SET_TOKENS,
    tokens,
  };
}

export function setCurrentItem(item) {
  return {
    type: mapbox.SET_CURRENT_ITEM,
    item,
  };
}

export function setFieldsGeojson(geojson) {
  return {
    type: mapbox.SET_FIELDS_GEOJSON,
    geojson,
  };
}

export function insertFeatureIntoGeojson(feature) {
  return {
    type: mapbox.INSERT_FIELDS_GEOJSON,
    feature,
  };
}

export function setRoute(item) {
  // Set route after fetch
  // adding additional properties
  return {
    type: mapbox.SET_ROUTE,
    item: {
      ...item,
      features: item.features.map((f) => {
        const featureCopy = { ...f };
        featureCopy.properties.selected = false;
        return featureCopy;
      }),
    },
  };
}

export function selectRouteSegment(index) {
  // set modified route
  return (dispatch, getState) => {
    const oldRoute = getState().mapbox.route;
    const item = {
      ...oldRoute,
      features: oldRoute.features.map((segment, sIndex) => {
        const segmentCopy = { ...segment };
        if (sIndex == index) {
          segmentCopy.properties.selected = !segmentCopy.properties.selected;
          // segment.properties.loaded = !segment.properties.loaded; //for test only
        } else {
          segmentCopy.properties.selected = false;
        }
        return segmentCopy;
      }),
    };

    dispatch({
      type: mapbox.SET_ROUTE,
      item,
    });
  };
}

export function clearRoute() {
  return {
    type: mapbox.SET_ROUTE,
    item: null,
  };
}

export function setFilterLoaded(filterState) {
  return {
    type: mapbox.SET_FILTER_LOADED,
    filterState,
  };
}

export function setStyle(index) {
  return {
    type: mapbox.SET_STYLE,
    index,
  };
}

export function setBounds(bounds) {
  return {
    type: mapbox.SET_BOUNDS,
    bounds,
  };
}

export function setTokenIsPending(tokenIsPending) {
  return {
    type: mapbox.SET_TOKEN_IS_PENDING,
    tokenIsPending,
  };
}

export function setGeojsonIsPending(geojsonIsPending) {
  return {
    type: mapbox.SET_FIELDS_GEOJSON_IS_PENDING,
    geojsonIsPending,
  };
}

/* API */

export const getMapboxToken = () => async (dispatch) => {
  dispatch(setTokenIsPending(true));
  try {
    const response = await commonGet({ url: urls().mapboxToken });
    dispatch(
      setTokens({
        token: response.data.token,
        bingmapstoken: response.data.bingmapstoken,
      })
    );
    return response.data;
  } finally {
    dispatch(setTokenIsPending(false));
  }
};

export const addFieldGeojson = (fieldId, season) => async (dispatch) => {
  dispatch(setGeojsonIsPending(true));
  try {
    const seasonParam = season ? `?season=${season}` : '';
    const response = await commonGet({
      url: `${urlsWrapper('fields')}/${fieldId}/geojson${seasonParam}`,
    });
    const { data } = response;
    const feature =
      data?.type === 'FeatureCollection' ? data.features?.[0] : data;
    if (feature) {
      dispatch(insertFeatureIntoGeojson(feature));
    }
    return data;
  } catch (e) {
    return [];
  } finally {
    dispatch(setGeojsonIsPending(false));
  }
};

// Read-only geometry loading for local map previews, without mutating the shared map.
export const getFieldsGeojsonData = async (season) => {
  const seasonParam = season ? `?season=${season}` : '';
  const response = await commonGet({
    url: `${urlsWrapper('fields')}/geojson${seasonParam}`,
  });
  return response.data;
};

export const getFieldsGeojson = (season) => async (dispatch) => {
  latestFieldsGeojsonRequestId += 1;
  const requestId = latestFieldsGeojsonRequestId;
  dispatch(setGeojsonIsPending(true));
  try {
    const data = await getFieldsGeojsonData(season);

    // Ignore stale responses when the user switches seasons quickly.
    if (requestId === latestFieldsGeojsonRequestId) {
      dispatch(setFieldsGeojson(data));
    }

    return data;
  } finally {
    if (requestId === latestFieldsGeojsonRequestId) {
      dispatch(setGeojsonIsPending(false));
    }
  }
};

// Группу хозяйств определяем по divisiongroup_id хозяйства. После перезагрузки
// страницы список хозяйств может ещё не прийти — тогда берём его из стора,
// а если и там пусто, запрашиваем сами, иначе карта уедет на границы всех полей.
const findDivision = async (divisionId, divisions, getState) => {
  const fromList = (list) =>
    (Array.isArray(list) ? list : []).find((d) => d.id == divisionId);

  const division = fromList(divisions) || fromList(getState().divisions?.list);
  if (division) {
    return division;
  }
  try {
    const response = await commonGet({ url: urlsWrapper('divisions') });
    return fromList(response?.data);
  } catch (e) {
    return null;
  }
};

export const getBounds =
  (divisionIds, divisions, season) => async (dispatch, getState) => {
    let url = `${urlsWrapper('bounds')}`;
    if (divisionIds?.length > 0) {
      const divisionId = divisionIds[0];
      if (divisionIds.length === 1) {
        if (divisionId === 0) {
          url = `${urlsWrapper('bounds')}`;
        } else {
          url = `${urlsWrapper('divisions')}/${divisionId}/fields/bounds`;
        }
      } else {
        const division = await findDivision(divisionId, divisions, getState);
        if (division && division.divisiongroup_id) {
          url = `${urlsWrapper('divisionGroups')}/${
            division.divisiongroup_id
          }/fields/bounds`;
        }
      }
    }
    const seasonParam = season
      ? `${url.includes('?') ? '&' : '?'}season=${season}`
      : '';
    try {
      const response = await commonGet({ url: `${url}${seasonParam}` });
      dispatch(setBounds(response.data));
      return response.data;
    } catch (e) {
      return null;
    }
  };

export const isValidMapBounds = (bounds) =>
  Array.isArray(bounds) &&
  (bounds.length === 2 || bounds.length === 4) &&
  bounds.every((num) => Number.isFinite(num));

export const saveBounds = (bounds) => async (dispatch, getState) => {
  if (!isValidMapBounds(bounds)) {
    return;
  }
  if (!isEqual(bounds, getState().mapbox.bounds)) {
    dispatch(setBounds(bounds));
  }
};

export const resetBounds = () => async (dispatch) => {
  dispatch(setBounds(defaultState.bounds));
};

export const geometryValidation = (geojson) =>
  commonPost({
    url: `${urlsWrapper('geometryValidation')}`,
    data: { geojson },
    name: 'валидация geojson',
  });

// selectors
export const getMapboxSelector = (state) => state.mapbox;
